<template v-cloak>
  <div>
    <div
      ref="cartDrawerOverlay"
      class="z-takeover fixed inset-0 bg-black/50"
      @click.self="handleCloseDrawer()"
    />
    <div
      ref="cartDrawer"
      class="z-takeover fixed top-0 right-0 flex h-full w-full max-w-[531px] flex-col overflow-y-auto bg-white shadow-xl"
    >
      <div class="flex flex-col items-center justify-between px-2.5 py-2.5">
        <div class="flex w-full items-center justify-between">
          <h2 class="text-xs font-medium uppercase" v-text="cartTitleText" />
          <p
            class="text-light-charcoal hidden text-center text-sm md:block"
            v-text="shippingMessage"
          />
          <button
            class="ml-auto cursor-pointer md:ml-0"
            aria-label="Close cart"
            @click="handleCloseDrawer()"
          >
            <Icon name="close" class-name="size-8" />
          </button>
        </div>
        <p class="text-light-charcoal text-center text-xs md:hidden" v-text="shippingMessage" />
        <!-- Free Shipping Threshold Bar -->
        <div v-if="props.showFreeShippingThreshold" class="w-full py-3">
          <div class="bg-natural-white h-[7px] w-full rounded-[4px]">
            <div ref="freeShippingThresholdBar" class="h-[7px] w-full rounded-[4px] bg-black" />
          </div>
        </div>
      </div>
      <div v-if="isLoading && predictedItemCount === 0">
        <empty-cart-skeleton
          :show-images="props.emptyCartDisplayImages && emptyCartLinks.length > 0"
          :button-count="emptyCartButtons.length"
        />
      </div>
      <div
        v-else-if="cart && (cart.item_count > 0 || predictedItemCount > 0)"
        class="flex h-full flex-col justify-between overflow-hidden"
      >
        <cart-items-skeleton
          v-if="(isLoading && !updatingLineItemId) || removingLineItemId"
          :item-count="skeletonItemCount"
        />
        <ul
          v-else-if="(!isLoading || updatingLineItemId) && !removingLineItemId"
          class="mb-auto overflow-y-auto px-2.5"
        >
          <li
            v-for="item in cart.items"
            :key="item.id"
            class="border-light-charcoal-0.3 mb-4 flex items-center justify-between border-b pb-4"
          >
            <div class="flex w-full items-center">
              <a class="block" :href="item.url">
                <img
                  :src="item.image"
                  :alt="item.product_title"
                  class="mr-4 aspect-square size-[106px] rounded object-contain"
                />
              </a>
              <div class="flex grow-1 flex-col gap-y-2.5">
                <div class="flex items-center justify-between">
                  <a
                    class="text-xs font-medium uppercase"
                    :href="item.url"
                    aria-label="View product"
                    v-text="
                      item.properties && item.properties._master_name
                        ? item.properties._master_name
                        : item.product_title
                    "
                  />
                </div>
                <p
                  v-if="item.properties.Message"
                  class="text-light-charcoal xs:w-[280px] w-[200px] truncate text-xs"
                  v-text="item.properties.Message"
                />
                <p
                  v-if="item.properties['Recipient email']"
                  class="text-light-charcoal xs:w-[280px] w-[200px] truncate text-xs"
                  v-text="`Email: ${item.properties['Recipient email']}`"
                />
                <p
                  v-if="item.properties['Send on']"
                  class="text-light-charcoal xs:w-[280px] w-[200px] truncate text-xs"
                  v-text="`Send on: ${item.properties['Send on']}`"
                />
                <p
                  v-if="item.properties._color_name"
                  class="font-regular text-light-charcoal xs:w-[280px] w-[200px] truncate text-xs"
                  v-text="`${props.colorLabel} ${item.properties._color_name}`"
                />
                <p
                  v-if="item.variant_title && item.variant_title !== 'Default Title'"
                  class="text-light-charcoal xs:w-[280px] w-[200px] truncate text-xs"
                  v-text="
                    `${item.gift_card || item.product_type === 'Gift Cards' ? 'Amount:' : props.sizeLabel} ${item.variant_title}`
                  "
                />
                <div class="flex items-center justify-between">
                  <cart-items-skeleton
                    v-if="updatingLineItemId === item.id"
                    :quantity-only="true"
                  />
                  <div
                    v-else
                    class="border-stroke flex min-h-[32px] min-w-[100px] items-center justify-around rounded-[20px] border text-xs font-medium text-black"
                  >
                    <button
                      class="cursor-pointer p-2"
                      aria-label="Decrease quantity"
                      @click="handleUpdateQuantity(item.id, item.quantity - 1, false)"
                    >
                      -
                    </button>
                    <span class="" v-text="item.quantity" />
                    <button
                      class="cursor-pointer p-2"
                      aria-label="Increase quantity"
                      :class="{ 'pointer-events-none opacity-50': item.quantity >= 10 }"
                      :disabled="item.quantity >= 10"
                      @click="handleUpdateQuantity(item.id, item.quantity + 1, true)"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex h-[112px] flex-col items-end gap-y-1">
                <div
                  v-if="item.original_price > item.final_price || item.properties._compare_at_price"
                  class="flex gap-x-2 text-sm font-medium"
                >
                  <p class="text-red" v-text="formatCurrency(item.final_price)" />
                  <p
                    class="text-light-charcoal line-through"
                    v-text="
                      formatCurrency(item.properties._compare_at_price || item.original_price)
                    "
                  />
                </div>
                <p
                  v-else
                  class="block text-sm font-medium"
                  v-text="formatCurrency(item.final_price)"
                />
                <p
                  v-if="item.properties._is_final_sale"
                  aria-label="Final Sale"
                  class="text-red block text-xs"
                >
                  Final Sale
                </p>
                <button
                  aria-label="Remove item"
                  title="Remove item"
                  class="mt-auto block cursor-pointer"
                  @click="handleRemoveItem(item.id)"
                >
                  <Icon name="close" class-name="size-8" />
                </button>
              </div>
            </div>
          </li>
        </ul>
        <cart-upsells
          :recommendations="recommendations"
          :size-label="props.sizeLabel"
          :upsells-add-label="props.upsellsAddLabel"
        />
        <cart-gift-note
          v-if="props.giftNoteEnabled"
          :gift-note-label="props.giftNoteLabel"
          :gift-note-title="props.giftNoteTitle"
        />
        <div class="border-light-charcoal-0.3 border-t bg-white px-2.5 py-3.5">
          <div class="mb-2 flex items-center justify-between font-semibold">
            <p v-text="props.subtotalLabel" />
            <p v-text="formatCurrency(cart.total_price)" />
          </div>
          <button
            id="cart-drawer-checkout-button"
            class="btn btn-secondary w-full hover:border-black"
            @click="handleCheckout"
            v-text="props.checkoutButtonLabel"
          />
        </div>
        <div v-if="paymentTypes.length > 0" class="overflow-hidden px-2.5 pb-3.5">
          <ul class="flex justify-between gap-2">
            <li v-for="payment in paymentTypes" :key="payment.name" class="w-full">
              <a
                class="btn w-full hover:border-black"
                :style="{ backgroundColor: payment.backgroundColor }"
                role="button"
                @click="handlePaymentMethodClick(payment.name)"
              >
                <img
                  :src="payment.logo"
                  :alt="payment.altText || payment.name"
                  class="h-4 w-full rounded object-contain px-3"
                />
              </a>
            </li>
          </ul>
        </div>
        <!-- </template> -->
      </div>
      <div v-else class="mt-14 px-14 text-center">
        <p class="mb-10 px-5 text-center text-xl md:px-10" v-text="props.emptyCartTitle" />
        <empty-cart-images
          v-if="props.emptyCartDisplayImages && emptyCartLinks.length"
          :links="emptyCartLinks"
          :handle-click="handleEmptyCartLinkClick"
        />
        <ul
          v-if="!props.emptyCartDisplayImages && emptyCartButtons.length"
          class="flex w-full flex-col items-center gap-y-5"
        >
          <li v-for="button in emptyCartButtons" :key="button.link_url" class="w-full">
            <button
              :key="button.link_url"
              class="btn btn-secondary w-full hover:border-black"
              @click="handleEmptyCartLinkClick(button.link_url)"
              v-text="button.link_text"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import gsap from 'gsap';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import {
  triggerOpenCartDrawer,
  triggerProductAdded,
  triggerProductRemoved,
} from '~/analytics/analytics-data';
import CartGiftNote from '~/components/cart-gift-note.vue';
import CartItemsSkeleton from '~/components/cart-items-skeleton.vue';
import CartUpsells from '~/components/cart-upsells.vue';
import emptyCartImages from '~/components/empty-cart-images.vue';
import emptyCartSkeleton from '~/components/empty-cart-skeleton.vue';
import Icon from '~/components/icon-svg.vue';
import { useCart } from '~/composables/use-cart';
import { useCartDrawerVisibility } from '~/composables/use-cart-drawer-visibility';
import { useCartPrediction } from '~/composables/use-cart-prediction';
import { useFocusManagement } from '~/composables/use-focus-management';
import { useScrollLock } from '~/composables/use-scroll-lock';
import { ANIMATION_EASES } from '~/constants/animations';
import DIGITAL_WALLETS_PROPS from '~/constants/payments';
import { TRACK_CART_SOURCE, TRACK_GENERIC_CTA_CLICKED_EVENT } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { updateCartContext } from '~/lib/dynamic-yield/dynamic-yield-context-manager';
import {
  sendDynamicYieldAddToCartEvent,
  sendDynamicYieldRemoveFromCartEvent,
} from '~/lib/dynamic-yield/dynamic-yield-events';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import { formatCurrency } from '~/utils/currency';
import { isApplePayDeviceEnabled } from '~/utils/device';
import { extractCartSkus } from '~/utils/item-cart-properties';
import * as LoopReturnsOnstoreUtils from '~/utils/loop-returns-onestore';
import { substitute } from '~/utils/string';
import { isBoolean, isNil } from '~/utils/validator';

const props = defineProps({
  addButtonText: {
    default: 'Add+',
    type: String,
  },
  checkoutButtonLabel: {
    default: 'Checkout',
    type: String,
  },
  colorLabel: {
    default: 'Color:',
    type: String,
  },
  emptyCartDisplayImages: {
    default: false,
    type: Boolean,
  },
  emptyCartLinks: {
    default: () => [],
    type: Array,
  },
  emptyCartTitle: {
    default: 'Your cart is empty. Start shopping!',
    type: String,
  },
  enabledPaymentTypes: {
    default: () => [],
    type: Array,
  },
  freeShippingMessageAchieved: {
    default: "You've earned free shipping!",
    type: String,
  },
  freeShippingMessageNotAchieved: {
    default: 'Spend {amount} more to earn free shipping!',
    type: String,
  },
  freeShippingThreshold: {
    default: 7500,
    type: Number,
  },
  giftNoteEnabled: {
    default: false,
    type: Boolean,
  },
  giftNoteLabel: {
    default: '',
    type: String,
  },
  giftNoteTitle: {
    default: '',
    type: String,
  },
  openCartDrawer: {
    default: false,
    type: Boolean,
  },
  showFreeShippingThreshold: {
    default: true,
    type: Boolean,
  },
  sizeLabel: {
    default: 'Size:',
    type: String,
  },
  subtotalLabel: {
    default: 'Subtotal',
    type: String,
  },
  title: {
    default: 'Cart',
    type: String,
  },
  upsellProducts: {
    default: () => [],
    type: Array,
  },
  upsellsAddLabel: {
    default: 'Add+',
    type: String,
  },
});

const { cart, getCart, isLoading, removeLineItem, updateSingleLineItem } = useCart();
const { predictedItemCount } = useCartPrediction();
const updatingLineItemId = ref(null);
const removingLineItemId = ref(null);
const { lockScroll, unlockScroll } = useScrollLock();
const visibility = useCartDrawerVisibility();
const recommendations = props.upsellProducts;

const emptyCartButtons = props.emptyCartLinks.filter((link) => link.link_text && link.link_url);
const emptyCartLinks = props.emptyCartLinks
  .filter((link) => link.image && link.link_url)
  .map((link) => ({
    image_alt: link.link_text || 'Image card',
    image_src: link.image + '&width=400',
    text: link.link_text,
    url: link.link_url,
  }));

const skeletonItemCount = computed(() => {
  if (!isLoading.value) return 0;

  // Use predicted count if available from operation
  if (predictedItemCount.value !== null) {
    return Math.max(0, predictedItemCount.value);
  }

  // Fallback: show 1 skeleton for initial load or empty cart scenarios
  if (!cart.value || cart.value.item_count === 0) {
    return 1;
  }

  // Use line item count (number of unique items) not total quantity
  return cart.value.items?.length || 0;
});

const cartTitleText = computed(() => `${props.title} (${cart.value?.item_count || 0})`);
const currentCartTotal = computed(() => cart.value?.total_price || 0);
const amountNeededForFreeShipping = computed(() =>
  Math.max(0, props.freeShippingThreshold - currentCartTotal.value),
);
const amountNeededForFreeShippingFormatted = computed(() =>
  formatCurrency(amountNeededForFreeShipping.value),
);
const freeShippingProgress = computed(() => {
  if (props.freeShippingThreshold === 0) return 1;

  return Math.min(1, currentCartTotal.value / props.freeShippingThreshold);
});
const shippingMessage = computed(() => {
  if (currentCartTotal.value >= props.freeShippingThreshold) {
    return props.freeShippingMessageAchieved;
  }
  if (amountNeededForFreeShipping.value > 0) {
    return substitute(props.freeShippingMessageNotAchieved, {
      amount: amountNeededForFreeShippingFormatted.value,
    });
  }

  return '';
});

const freeShippingThresholdBar = ref(null);
const cartDrawer = ref(null);
const cartDrawerOverlay = ref(null);
let shippingBarTimeline = null;
let inViewTimeline = null;
const paymentTypes = ref([]);

useFocusManagement({
  containerRef: cartDrawer,
  focusFirstOnActivate: ref(true),
  isActive: visibility.isOpen,
  trapFocus: true,
});

const setupShippingBarTimeline = () => {
  if (!freeShippingThresholdBar.value) return;

  if (shippingBarTimeline) {
    shippingBarTimeline.kill();
    shippingBarTimeline.clear();
    shippingBarTimeline = null;
  }

  shippingBarTimeline = gsap.timeline({ paused: true });
};

const setupPaymentTypes = () => {
  const applePayEnabled = isApplePayDeviceEnabled();
  let paymentTypesArray = props.enabledPaymentTypes
    .map((payment) => {
      return DIGITAL_WALLETS_PROPS[payment]
        ? {
            ...DIGITAL_WALLETS_PROPS[payment],
            name: payment,
          }
        : null;
    })
    .filter(
      (payment) =>
        payment !== null &&
        (applePayEnabled ? payment.isApplePayFriendly === applePayEnabled : !payment.isApplePay),
    );

  paymentTypes.value = paymentTypesArray;
};

const setupInViewTimeline = () => {
  if (!cartDrawer.value) return;

  if (inViewTimeline) {
    inViewTimeline.kill();
    inViewTimeline.clear();
    inViewTimeline = null;
  }

  inViewTimeline = gsap.timeline({
    onComplete: () => {
      if (!isLoading.value) {
        transitionInShippingBar(freeShippingProgress.value);
      }
    },
    onReverseComplete: () => {
      unlockScroll();
    },
    paused: true,
  });

  gsap.set(cartDrawerOverlay.value, { opacity: 0, pointerEvents: 'none' });
  gsap.set(cartDrawer.value, { xPercent: 100 });

  inViewTimeline.to(
    cartDrawerOverlay.value,
    {
      duration: 0.4,
      ease: ANIMATION_EASES.SINE_OUT,
      opacity: 1,
      pointerEvents: 'auto',
    },
    0,
  );

  inViewTimeline.to(
    cartDrawer.value,
    {
      duration: 0.8,
      ease: ANIMATION_EASES.POWER3_OUT,
      xPercent: 0,
    },
    0,
  );
};

const handleEscapeKeydown = (event) => {
  if (event.key === 'Escape') {
    handleCloseDrawer();
  }
};

const setupEventListeners = () => {
  window.addEventListener('keydown', handleEscapeKeydown);
};

const removeEventListeners = () => {
  window.removeEventListener('keydown', handleEscapeKeydown);
};

const transitionInShippingBar = (progress) => {
  if (!shippingBarTimeline) return;

  shippingBarTimeline.to(freeShippingThresholdBar.value, {
    duration: 0.6,
    ease: ANIMATION_EASES.POWER3_IN_OUT,
    scaleX: progress,
    transformOrigin: 'left',
  });
  shippingBarTimeline.play();
};

const transitionInCartDrawer = () => {
  if (!inViewTimeline) return;

  if (visibility.isOpen.value) {
    inViewTimeline.play();
  }
};

const handleRemoveItem = async (lineId) => {
  // Analytics, pull the removed product before removing it from the cart
  const productRemoved = cart?.value?.items.find((item) => item.id === lineId);
  const currentQuantity = productRemoved?.quantity;

  // Don't set loading state if already called from handleUpdateQuantity
  if (removingLineItemId.value !== lineId) {
    removingLineItemId.value = lineId;
  }

  try {
    // Remove the entire product from the cart
    await removeLineItem(lineId);

    // Send Dynamic Yield remove-from-cart event
    if (productRemoved) {
      sendDynamicYieldRemoveFromCartEvent({
        price: productRemoved.price / 100, // Convert cents to dollars
        productId: productRemoved.sku,
        quantity: currentQuantity,
      });
    }

    const cartSkus = extractCartSkus(cart.value);
    updateCartContext(cartSkus);

    // Analytics, send the removed product with current Cart
    triggerProductRemoved(cart.value, productRemoved, currentQuantity);
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds component] - Cart Drawer - handleRemoveItem - Error removing item',
      {
        currentQuantity,
        productRemoved,
      },
      error,
    );
  } finally {
    // Clear loading states if they're set for this item
    if (removingLineItemId.value === lineId) {
      removingLineItemId.value = null;
    }
    if (updatingLineItemId.value === lineId) {
      updatingLineItemId.value = null;
    }
  }
};

const handleUpdateQuantity = async (lineId, newQuantity, isAdding) => {
  if (isNil(lineId) || isNil(newQuantity) || !isBoolean(isAdding)) {
    DataDogLogs.error(
      '[Allbirds component] - Cart Drawer - handleUpdateQuantity - No lineId or newQuantity or isAdding',
      {
        isAdding,
        lineId,
        newQuantity,
      },
      new Error('No lineId or newQuantity or isAdding'),
    );

    return;
  }

  // Check if we're removing the item (going from 1 to 0)
  const currentItem = cart?.value?.items.find((item) => item.id === lineId);
  const isRemoving = currentItem?.quantity === 1 && newQuantity === 0;

  if (isRemoving) {
    // Show full skeleton when removing
    removingLineItemId.value = lineId;
  } else {
    // Show quantity-only skeleton when updating
    updatingLineItemId.value = lineId;
  }

  try {
    if (newQuantity <= 0) {
      // If quantity is 0, means last item, then remove the entire product
      await handleRemoveItem(lineId);
    } else {
      // Analytics, pull the added/removed product before updating the quantity
      const productToUpdate = cart?.value?.items.find((item) => item.id === lineId);

      try {
        // Update quantity on existing product on cart
        await updateSingleLineItem(lineId, newQuantity);

        // Analytics, send the added/removed product
        if (isAdding) {
          // Send Dynamic Yield add-to-cart event for quantity increase
          sendDynamicYieldAddToCartEvent({
            price: productToUpdate.price / 100, // Convert cents to dollars
            productId: productToUpdate.sku,
            quantity: 1, // Quantity change is always 1 for increment
          });

          triggerProductAdded(
            cart.value,
            productToUpdate,
            TRACK_CART_SOURCE.CART,
            productToUpdate?.variant_title,
          );
        } else {
          // Send Dynamic Yield remove-from-cart event for quantity decrease
          sendDynamicYieldRemoveFromCartEvent({
            price: productToUpdate.price / 100, // Convert cents to dollars
            productId: productToUpdate.sku,
            quantity: 1, // Quantity change is always 1 for decrement
          });

          triggerProductRemoved(cart.value, productToUpdate, newQuantity);
        }

        const cartSkus = extractCartSkus(cart.value);
        updateCartContext(cartSkus);
      } catch (error) {
        DataDogLogs.error(
          '[Allbirds component] - Cart Drawer - handleUpdateQuantity - Error updating quantity',
          {
            isAdding,
            newQuantity,
            productToUpdate,
          },
          error,
        );
      }
    }
  } finally {
    updatingLineItemId.value = null;
    removingLineItemId.value = null;
  }

  transitionInShippingBar(freeShippingProgress.value);
};

const getCheckoutUrl = () => window.Shopify.routes.root + 'checkout';

const handleCheckout = () => {
  // If Loop Returns OnStore is active, use Loop Returns checkout
  if (LoopReturnsOnstoreUtils.isActive()) {
    DataDogLogs.warn(
      '[Allbirds component] - Cart Drawer - handleCheckout - Loop Returns OnStore is active - using Loop Returns checkout',
    );

    return;
  }

  // By default, use Shopify checkout
  window.location.href = getCheckoutUrl();
};

/**
 * Handle the click on a payment method button
 *
 * Used navigaion delay workaround for the Analytics, 'Query String API' could not be used because destination is outside the website
 *
 * @param {string} paymentMethodName - The name of the payment method
 * @returns {void}
 */
const handlePaymentMethodClick = (paymentMethodName) => {
  if (paymentMethodName) {
    RudderstackClient.navigationDelayForIphoneAndSafari({
      analyticsFn: () =>
        RudderstackClient.callToActionClicked({
          location: TRACK_GENERIC_CTA_CLICKED_EVENT.LOCATIONS.CART,
          name: DIGITAL_WALLETS_PROPS[paymentMethodName].analyticsKey,
          type: TRACK_GENERIC_CTA_CLICKED_EVENT.TYPES.BUTTON,
          url: getCheckoutUrl(),
        }),
      openInNewTab: false,
      urlToNavigate: getCheckoutUrl(),
    });
  } else {
    DataDogLogs.error(
      '[Allbirds component] - Cart Drawer - handlePaymentMethodClick - No payment method name provided',
      {
        paymentMethodName,
      },
      new Error('No payment method name'),
    );
  }
};

const handleEmptyCartLinkClick = (url) => {
  handleCloseDrawer();
  if (url) {
    window.location.href = url;
  }
};

const handleCloseDrawer = () => {
  visibility.closeDrawer();
  inViewTimeline.reverse();
};

watch(isLoading, (isLoadingNew) => {
  if (!isLoadingNew && cart.value) {
    transitionInShippingBar(freeShippingProgress.value);
  }
});

watch(visibility.isOpen, (newVal) => {
  if (newVal) {
    lockScroll();
    setupShippingBarTimeline();
    transitionInCartDrawer();
    triggerOpenCartDrawer(cart?.value?.items);
  }
});

onMounted(async () => {
  setupInViewTimeline();
  setupEventListeners();
  setupPaymentTypes();

  if (!cart.value && !isLoading.value) {
    try {
      await getCart();
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds component] - Cart Drawer - onMounted - Error getting cart',
        {
          cart,
        },
        error,
      );
    }
  }

  gsap.set(freeShippingThresholdBar.value, {
    scaleX: freeShippingProgress.value,
    transformOrigin: 'left',
  });

  if (props.openCartDrawer) {
    visibility.openDrawer();
  }
});

onUnmounted(() => {
  removeEventListeners();
});
</script>
